import { ApiClientAxios, apiFactoryConfig, ApiFactoryConfigTuple } from "@repo/api";
import { keepPreviousData, useInfiniteQuery, type InfiniteData } from "@tanstack/react-query";
import axios from "axios";
import { ms } from "humanize-ms";
import { CustomApiError } from "../constants/customApiError";

// Infinite query hook for registered users using pageNumber-based pagination (1-based)
export type RegisteredUsersResult = {
    nextPage: boolean;
    items: ApiClientAxios.RegisteredUser[];
    pageNumber: number;
};

export interface UseInfiniteQueryRegisteredUsersParams<TSelected = RegisteredUsersResult> {
    search: string;
    enabled?: boolean;
    /** Override the default (B2C) apiFactoryConfig – e.g. with Entra ID config from callcenter. */
    apiConfig?: ApiFactoryConfigTuple;
    // Optional select transformer to optimize renders at the component level
    select?: (data: InfiniteData<RegisteredUsersResult>) => TSelected;
}

const COUNT_ITEMS = 10; // items per page

export const useInfiniteQueryRegisteredUsers = <TSelected = RegisteredUsersResult>({
    search,
    enabled = true,
    apiConfig,
    select,
}: UseInfiniteQueryRegisteredUsersParams<TSelected>) => {
    const api = ApiClientAxios.CallCenterV2ApiFactory(...(apiConfig ?? apiFactoryConfig));

    return useInfiniteQuery<RegisteredUsersResult, unknown, TSelected>({
        queryKey: ["registeredUsersInfinite", { search }],
        // pageParam represents pageNumber (1-based)
        queryFn: async ({ pageParam = 1 }) => {
            const page = typeof pageParam === "number" ? pageParam : 1;
            try {
                const raw: ApiClientAxios.GetRegisteredUsersResponse =
                    // API signature: getRegisteredUsersV2Get(search?: string, count?: number, pageNumber?: number)
                    // We must pass COUNT before pageNumber; !otherwise page was incorrectly sent as count!
                    (await api.getRegisteredUsersV2Get(search || undefined, COUNT_ITEMS, page)).data;
                const nextPage = !!raw.nextPage;
                const items = Array.isArray(raw.items) ? raw.items : [];
                const pageNumber = page;
                return { nextPage: nextPage, items, pageNumber };
            } catch (err) {
                // If backend returns 404 -> treat as "nothing found"
                if (axios.isAxiosError(err) && err.response?.status === CustomApiError.NotFound) {
                    return { nextPage: false, items: [], pageNumber: page };
                }
                throw err;
            }
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            if (!lastPage.nextPage) return undefined;
            // Increment pageNumber by 1 while backend reports HasMore
            return (lastPage.pageNumber ?? 1) + 1;
        },
        gcTime: ms("1h"),
        staleTime: ms("1h"),
        enabled: enabled && !!search?.trim(),
        placeholderData: keepPreviousData,
        select,
    });
};

// Helper to flatten all pages
export const flattenRegisteredUsersPages = (
    data?: { pages: Array<RegisteredUsersResult> } | InfiniteData<RegisteredUsersResult>,
) => data?.pages.flatMap((p) => p.items) ?? [];
